<div class="patient-detail-container">
  <!-- Loading State -->
  <div *ngIf="isLoading" class="loading-container">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">{{ "loading" | translate }}...</span>
    </div>
    <p class="loading-text">{{ "loading" | translate }}...</p>
  </div>

  <!-- Error State -->
  <div *ngIf="hasError && !isLoading" class="error-container">
    <i class="fa-solid fa-triangle-exclamation error-icon"></i>
    <p class="error-text">{{ "patientDetail.noData" | translate }}</p>
    <button class="btn btn-primary" (click)="goBack()">
      {{ "backToMenu" | translate }}
    </button>
  </div>

  <!-- Main Content -->
  <ng-container *ngIf="!isLoading && !hasError && patient">
    <!-- HEADER: Patient Summary (Shared Component) -->
    <app-patient-overview [patient]="patient"></app-patient-overview>

    <!-- MAIN CONTENT: Two Columns -->
    <div class="main-content">
      <!-- LEFT: Patient Detail Card -->
      <div class="detail-card">
        <div class="card-header">
          <span>{{ "patientDetail.title" | translate }}</span>
          <button
            class="btn-ai-summary"
            (click)="generateSummary()"
            [disabled]="isGeneratingSummary"
          >
            <i class="fa-solid" [class.fa-wand-magic-sparkles]="!isGeneratingSummary" [class.fa-spinner]="isGeneratingSummary" [class.fa-spin]="isGeneratingSummary"></i>
            {{ (isGeneratingSummary ? 'patientDetail.aiSummary.generating' : (aiSummary ? 'patientDetail.aiSummary.regenerate' : 'patientDetail.aiSummary.generateBtn')) | translate }}
          </button>
        </div>

        <!-- AI Summary Panel -->
        <div class="ai-summary-panel" *ngIf="aiSummary || summaryError">
          <div class="ai-summary-error" *ngIf="summaryError">
            <i class="fa-solid fa-circle-exclamation"></i>
            {{ 'patientDetail.aiSummary.error' | translate }}
          </div>
          <div class="ai-summary-content" *ngIf="aiSummary && !summaryError">
            <div class="ai-summary-header">
              <i class="fa-solid fa-robot"></i>
              <span>{{ 'patientDetail.aiSummary.title' | translate }}</span>
            </div>
            <p class="ai-summary-text">{{ aiSummary }}</p>
          </div>
        </div>

        <div class="card-body">
          <!-- PATIENT DETAIL TAB CONTENT -->
          <div *ngIf="activeDetailTab === 'patient'">
            <!-- Basic Info -->
            <div class="detail-row" *ngIf="patient.email">
              <span class="detail-label">{{ "email" | translate }}</span>
              <span class="detail-value">{{ patient.email }}</span>
            </div>
            <div class="detail-row" *ngIf="patient.phoneNumber">
              <span class="detail-label">{{ "phoneNumber" | translate }}</span>
              <span class="detail-value">
                {{ patient.countryCode || "" }} {{ patient.phoneNumber }}
              </span>
            </div>
            <div class="detail-row" *ngIf="patient.dietType">
              <span class="detail-label">{{ "dietType" | translate }}</span>
              <span class="detail-value">
                {{ getDietTypeText(patient.dietType) }}
              </span>
            </div>
            <div class="detail-row" *ngIf="patient.maritalStatus">
              <span class="detail-label">{{
                "maritalStatus" | translate
              }}</span>
              <span class="detail-value">
                {{ getMaritalStatusText(patient.maritalStatus) }}
              </span>
            </div>
            <div class="detail-row" *ngIf="patient.skinType">
              <span class="detail-label">{{ "skinType" | translate }}</span>
              <span class="detail-value">
                {{ getSkinTypeText(patient.skinType) }}
              </span>
            </div>
            <div class="detail-row" *ngIf="patient.bloodType">
              <span class="detail-label">{{ "bloodType" | translate }}</span>
              <span class="detail-value">
                {{ getBloodTypeText(patient.bloodType) }}
              </span>
            </div>
            <div
              class="detail-row"
              *ngIf="patient.smokingInformation?.smokingStatus"
            >
              <span class="detail-label">{{
                "smokingStatus" | translate
              }}</span>
              <span class="detail-value">
                {{
                  getSmokingStatusText(patient.smokingInformation.smokingStatus)
                }}
              </span>
            </div>
            <div class="detail-row" *ngIf="patient.alcoholConsumption?.type">
              <span class="detail-label">{{
                "alcoholConsumptiontype" | translate
              }}</span>
              <span class="detail-value">
                {{ getAlcoholConsumptionText(patient.alcoholConsumption.type) }}
              </span>
            </div>
            <div class="detail-row" *ngIf="patient.livingConditions">
              <span class="detail-label">{{
                "livingConditions" | translate
              }}</span>
              <span class="detail-value">
                {{ getLivingConditionsText(patient.livingConditions) }}
              </span>
            </div>
            <div class="detail-row" *ngIf="patient.height && patient.weight">
              <span class="detail-label">{{ "bmi" | translate }}</span>
              <span class="detail-value">
                {{ calculateBMI(patient.height, patient.weight) }}
              </span>
            </div>
            <div
              class="detail-row"
              *ngIf="
                patient.smokingInformation?.amountPerDay &&
                patient.smokingInformation?.durationInYears
              "
            >
              <span class="detail-label">{{
                "patientCreate.form.packYearFormula" | translate
              }}</span>
              <span class="detail-value">
                {{
                  ((patient.smokingInformation.amountPerDay || 0) / 20) *
                    (patient.smokingInformation.durationInYears || 0)
                    | number: "1.2-2"
                }}
              </span>
            </div>

            <!-- ABI Section -->
            <div class="detail-row" *ngIf="true">
              <span class="detail-label">{{ "patientDetail.abiQuestion" | translate }}</span>
              
              <!-- Display mode: Show value on the right (always visible, toggles edit mode) -->
              <div class="abi-display-section" [class.active]="abiEditMode" (click)="enableABIEdit()" role="button" [attr.tabindex]="0">
                <span class="detail-value abi-value">
                  {{ initialAbiValue === null ? ("patientDetail.abi.unset" | translate) : (initialAbiValue === 0 ? ("patientDetail.abi.no" | translate) : getABIValueText(initialAbiValue)) }}
                </span>
              </div>
            </div>

            <!-- ABI Edit Section (expands below the main row) -->
            <div class="abi-edit-section" *ngIf="abiEditMode">
              <div class="detail-row abi-section">
                <span class="detail-label"></span>
                
                <!-- Edit mode: Radio buttons -->
                <div class="abi-options">
                  <label class="abi-radio">
                    <input
                      type="radio"
                      name="abiMeasured"
                      [checked]="currentAbiValue === null"
                      (change)="toggleAbiOption(null)"
                      [disabled]="isAbiSaving"
                    />
                    <span>{{ "patientDetail.abi.unset" | translate }}</span>
                  </label>
                  <label class="abi-radio">
                    <input
                      type="radio"
                      name="abiMeasured"
                      [checked]="isAbiOptionSelected(0)"
                      (change)="toggleAbiOption(0)"
                      [disabled]="isAbiSaving"
                    />
                    <span>{{ "patientDetail.abi.no" | translate }}</span>
                  </label>
                  <label class="abi-radio">
                    <input
                      type="radio"
                      name="abiMeasured"
                      [checked]="isAbiOptionSelected(1) || isAbiOptionSelected(2) || isAbiOptionSelected(3) || isAbiOptionSelected(4)"
                      (change)="toggleAbiOption(isAbiVisible ? null : 1)"
                      [disabled]="isAbiSaving"
                    />
                    <span>{{ "patientDetail.abi.yes" | translate }}</span>
                  </label>
                </div>
              </div>

              <!-- ABI Categories (only visible when Yes is selected) -->
              <div class="abi-categories" *ngIf="isAbiVisible">
                <div class="abi-category-grid">
                  <label class="abi-category-radio">
                    <input
                      type="radio"
                      name="abiValue"
                      [checked]="isAbiOptionSelected(1)"
                      (change)="toggleAbiOption(1)"
                      [disabled]="isAbiSaving"
                    />
                    <span>{{ "patientDetail.abi.normal" | translate }}</span>
                  </label>
                  <label class="abi-category-radio">
                    <input
                      type="radio"
                      name="abiValue"
                      [checked]="isAbiOptionSelected(2)"
                      (change)="toggleAbiOption(2)"
                      [disabled]="isAbiSaving"
                    />
                    <span>{{ "patientDetail.abi.borderline" | translate }}</span>
                  </label>
                  <label class="abi-category-radio">
                    <input
                      type="radio"
                      name="abiValue"
                      [checked]="isAbiOptionSelected(3)"
                      (change)="toggleAbiOption(3)"
                      [disabled]="isAbiSaving"
                    />
                    <span>{{ "patientDetail.abi.abnormal" | translate }}</span>
                  </label>
                  <label class="abi-category-radio">
                    <input
                      type="radio"
                      name="abiValue"
                      [checked]="isAbiOptionSelected(4)"
                      (change)="toggleAbiOption(4)"
                      [disabled]="isAbiSaving"
                    />
                    <span>{{ "patientDetail.abi.noncompressible" | translate }}</span>
                  </label>
                </div>
              </div>

              <!-- Save/Cancel Buttons -->
              <div class="abi-actions" *ngIf="hasAbiChanged">
                <button
                  class="btn btn-sm btn-primary"
                  (click)="saveABIValue()"
                  [disabled]="isAbiSaving"
                >
                  <span *ngIf="!isAbiSaving">{{ "save" | translate }}</span>
                  <span *ngIf="isAbiSaving">
                    <i class="fa-solid fa-spinner fa-spin"></i>
                  </span>
                </button>
              </div>
            </div>

            <div class="detail-row" *ngIf="patient.placeOfResidence">
              <span class="detail-label">
                {{ "patientDetail.placeOfResidence" | translate }}
              </span>
              <span class="detail-value">{{ patient.placeOfResidence }}</span>
            </div>

            <!-- Physical Info -->
            <div class="detail-row" *ngIf="patient.height || patient.weight">
              <span class="detail-label">
                {{ "height" | translate }} / {{ "weight" | translate }}
              </span>
              <span class="detail-value">
                {{ patient.height ? patient.height + " cm" : "-" }} /
                {{ patient.weight ? patient.weight + " kg" : "-" }}
              </span>
            </div>

            <!-- Medications -->
            <div
              class="detail-row list-row"
              *ngIf="patient.medicationDto?.length"
            >
              <span class="detail-label">{{ "medications" | translate }}</span>
              <div class="detail-value-list">
                <span
                  class="tag-item"
                  *ngFor="let med of patient.medicationDto"
                >
                  {{ med.name }}
                </span>
              </div>
            </div>

            <!-- Antibiotics -->
            <div
              class="detail-row list-row"
              *ngIf="patient.antibioticDto?.length"
            >
              <span class="detail-label">
                {{ "patientDetail.antibiotics" | translate }}
              </span>
              <div class="detail-value-list">
                <span
                  class="tag-item"
                  *ngFor="let antibiotic of patient.antibioticDto"
                >
                  {{ antibiotic.name }}
                </span>
              </div>
            </div>

            <!-- Allergies -->
            <div class="detail-row list-row" *ngIf="patient.allergyDto?.length">
              <span class="detail-label">
                {{ "patientDetail.allergies" | translate }}
              </span>
              <div class="detail-value-list">
                <span
                  class="tag-item"
                  *ngFor="let allergy of patient.allergyDto"
                >
                  {{ allergy.name }}
                </span>
              </div>
            </div>

            <!-- Surgeries -->
            <div class="detail-row list-row" *ngIf="patient.surgeryDto?.length">
              <span class="detail-label">
                {{ "patientDetail.surgeries" | translate }}
              </span>
              <div class="detail-value-list">
                <span
                  class="tag-item"
                  *ngFor="let surgery of patient.surgeryDto"
                >
                  {{ surgery.name }}
                </span>
              </div>
            </div>

            <!-- Debridements -->
            <div
              class="detail-row list-row"
              *ngIf="patient.debridementDto?.length"
            >
              <span class="detail-label">
                {{ "patientDetail.debridement" | translate }}
              </span>
              <div class="detail-value-list">
                <span
                  class="tag-item"
                  *ngFor="let debridement of patient.debridementDto"
                >
                  {{ debridement.name }}
                </span>
              </div>
            </div>

            <!-- Wound Care Products -->
            <div
              class="detail-row list-row"
              *ngIf="patient.woundCareProductDto?.length"
            >
              <span class="detail-label">
                {{ "patientDetail.woundCareProducts" | translate }}
              </span>
              <div class="detail-value-list">
                <span
                  class="tag-item"
                  *ngFor="let product of patient.woundCareProductDto"
                >
                  {{ product.name }}
                </span>
              </div>
            </div>
          </div>

          <!-- Empty Patient state if no selection or data -->
          <div
            class="empty-state"
            *ngIf="
              activeDetailTab === 'patient' &&
              !patient.medicationDto?.length &&
              !patient.antibioticDto?.length &&
              !patient.allergyDto?.length &&
              !patient.surgeryDto?.length &&
              !patient.debridementDto?.length &&
              !patient.woundCareProductDto?.length &&
              !patient.email &&
              !patient.phoneNumber
            "
          >
            <i class="fa-solid fa-file-medical"></i>
            <p>{{ "patientDetail.noData" | translate }}</p>
          </div>
        </div>
      </div>

      <!-- RIGHT: Chat / Lab Results Panel -->
      <div class="tabs-panel">
        <div class="tab-header">
          <button
            class="tab-btn"
            [class.active]="activeTab === 'chat'"
            (click)="setActiveTab('chat')"
          >
            {{ "patientDetail.chat" | translate }}
          </button>
          <button
            class="tab-btn"
            [class.active]="activeTab === 'documents'"
            (click)="setActiveTab('documents')"
          >
            {{ "documents.title" | translate }}
          </button>
        </div>
        <div class="tab-content">
          <!-- Chat Content -->
          <div *ngIf="activeTab === 'chat'" class="tab-pane chat-pane">
            <app-chat
              [groupId]="patientId"
              [assignedDoctorIds]="doctorIds"
              [patientInfo]="patient"
            ></app-chat>
          </div>

          <!-- Documents Content -->
          <div
            *ngIf="activeTab === 'documents'"
            class="tab-pane documents-pane"
          >
            <app-document-manager
              [patientId]="patientId"
              [wounds]="wounds"
              [groupByWound]="true"
              [readOnlyDocumentUpload]="true"
            ></app-document-manager>
          </div>
        </div>
      </div>
    </div>

    <!-- BOTTOM: Wounds Timeline -->
    <app-wound-timeline
      [wounds]="wounds"
      [patientId]="patientId"
      (woundArchived)="loadWoundsData()"
      (woundUnarchived)="loadWoundsData()"
    ></app-wound-timeline>
  </ng-container>
</div>
